import { roundTimestampToRange } from '../../../helpers/range';
import { useApiSegmentsRange } from '../../hooks/api/useApiSegmentsRange';
import { useApiThumbnailsRange } from '../../hooks/api/useApiThumbnailsRange';
import { ThumbnailDisplayInfo, ThumbnailImageMetadata } from '../types';
import { generateFallbackThumbnailDisplayInfo } from '../utils';

export interface UseThumbnailOptions {
  /** Length of range (minutes) to fetch when range is not provided. */
  fetchRangeMinutes?: number;
  /** Explicit range to fetch. When provided, used instead of fetchRangeMinutes for stable cache keys (e.g. hover over waveform). */
  range?: { from: number; to: number };
}

export type UseThumnailResult = {
  isLoading: boolean;
  info: ThumbnailDisplayInfo | null;
};

/**
 * Hook that returns ThumbnailDisplayInfo for a given timestamp.
 * It will asks the API for segments/thumbnails and return the found
 * thumbnail info.
 * Ideally, in the future it'll the API the one that provides
 * the thumbnail URL in the segment response.
 *
 * fetchRangeMinutes indicates the length of range that will be
 * fetch from the API. A larger range allows data to be cached by
 * the browser and React Query.
 * When range is provided, that range is used instead so all timestamps
 * in the same range hit the same cache (e.g. hover uses visible segments range).
 */
export function useThumbnailForTs(
  stationId: string,
  ts: number,
  { fetchRangeMinutes = 1, range }: UseThumbnailOptions = {
    fetchRangeMinutes: 1,
  },
): UseThumnailResult {
  const [from, to] = range ? [range.from, range.to] : roundTimestampToRange(ts, fetchRangeMinutes);

  const segmentsQuery = useApiSegmentsRange(stationId, from, to);
  const thumbnailsQuery = useApiThumbnailsRange(stationId, from, to);

  if (!thumbnailsQuery.data || !segmentsQuery.data) {
    return {
      isLoading: true,
      info: null,
    };
  }

  // reverse order for overlapped segments workaround
  const segment = segmentsQuery.data
    .slice()
    .reverse()
    .find((s) => ts >= s.startTs && ts < s.endTs);

  if (!segment) {
    console.log('[useThumbnailForTs] info null: no segment found for ts in range', {
      stationId,
      ts,
      from,
      to,
      segmentsCount: segmentsQuery.data.length,
      segmentRanges: segmentsQuery.data.map((s) => ({
        id: s.id,
        startTs: s.startTs,
        endTs: s.endTs,
      })),
    });
    return {
      isLoading: false,
      info: null,
    };
  }

  const thumbnailData = thumbnailsQuery.data.find((t) => t.segmentId === segment.id);

  if (!thumbnailData) {
    console.log('[useThumbnailForTs] info null: no thumbnail for segment', {
      stationId,
      ts,
      segmentId: segment.id,
      from,
      to,
      thumbnailSegmentIds: thumbnailsQuery.data.map((t) => t.segmentId),
    });
    return {
      isLoading: false,
      info: null,
    };
  }

  const exactSecondInSegment = (ts - segment.startTs) / 1000;

  // Use nearest second for tighter visual sync, then clamp to available thumbnail slots.
  // Most segments expose one thumbnail per whole second: [0..floor(duration)-1].
  const maxOffset = Math.max(0, Math.floor(segment.duration / 1000) - 1);
  const nearestSecondInSegment = Math.round(exactSecondInSegment);
  const adjustedOffset = Math.min(maxOffset, Math.max(0, nearestSecondInSegment));

  // if the API does not provide metadata for the segment,
  // simulate metadata using its duration
  const metadata: ThumbnailImageMetadata =
    segment.metadata?.thumbnails || generateFallbackThumbnailDisplayInfo(segment.duration);

  return {
    isLoading: false,
    info: {
      url: thumbnailData.thumbnailUrl,
      offset: adjustedOffset,
      metadata,
    },
  };
}
